// In development, a previously-registered service worker can poison the module
// loader by returning cached images instead of JS chunks (MIME mismatch →
// "Failed to fetch dynamically imported module"). Calling unregister() alone
// is not enough: the already-active SW keeps intercepting fetches for the
// current page until a full reload. So we: unregister → wipe caches → reload.
// A sessionStorage guard prevents an infinite reload loop.

export default defineNuxtPlugin(() => {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  // In production, workbox-window periodically fetches /sw.js to check for
  // updates (periodicSyncForUpdates). If the user is offline at that moment
  // the fetch rejects with TypeError: Failed to fetch — an unhandled rejection
  // that Sentry reports as a real error. Suppress it gracefully.
  if (!import.meta.dev) {
    window.addEventListener("unhandledrejection", (event) => {
      const reason = event.reason;
      if (!(reason instanceof Error)) return;

      // TypeError: Failed to fetch — workbox-window offline update check
      // or Nuxt's /_nuxt/builds/latest.json polling failing during deploys.
      // event.preventDefault() MUST be called synchronously; an async
      // .then() is too late and Sentry captures the error before it resolves.
      if (reason instanceof TypeError && reason.message === "Failed to fetch") {
        event.preventDefault();
        return;
      }

      // WebKit/iOS Safari equivalent of "Failed to fetch": when
      // navigator.serviceWorker.register('/sw.js') can't load the script
      // (offline, flaky network, mid-deploy), Safari/Chrome-iOS throws
      // `TypeError: Script https://.../sw.js load failed` instead of Chrome's
      // wording. Same harmless cause — suppress it the same way.
      if (
        reason instanceof TypeError &&
        /load failed/i.test(reason.message) &&
        reason.message.includes("sw.js")
      ) {
        event.preventDefault();
        return;
      }

      // InvalidStateError — two known variants:
      // 1. Chrome desktop: registration.update() called while page is navigating
      //    (message contains "ServiceWorker" + "invalid state")
      // 2. Chrome Mobile iOS: registration.update() called when newestWorker
      //    is null (iOS SW lifecycle quirk — message: "newestWorker is null")
      // Both are harmless browser-internal races during page load / deploy.
      if (reason.name === "InvalidStateError") {
        event.preventDefault();
        return;
      }
    });
    return;
  }

  // Only run the dev cleanup below in development.

  // Guard: if we already did a forced reload this session, don't loop.
  if (sessionStorage.getItem("sw_cleanup_done") === "1") return;

  navigator.serviceWorker.getRegistrations().then(async (registrations) => {
    if (registrations.length === 0) return; // Nothing to do.

    console.log(
      `[ServiceWorker] Dev mode: unregistering ${registrations.length} SW(s) and wiping caches…`,
    );

    // 1. Unregister all SWs.
    await Promise.all(registrations.map((r) => r.unregister()));

    // 2. Delete every cache the SW created.
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));

    // 3. Mark that we've done the cleanup so the reload doesn't loop.
    sessionStorage.setItem("sw_cleanup_done", "1");

    // 4. Hard reload — on this next load there is no registered SW,
    //    so _nuxt/* chunks are fetched directly from the Vite dev server.
    window.location.reload();
  });
});
